/**
 * 検査準備ページ
 *
 * 検査設定の確認と検査開始を行う画面
 */

import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ExamPrepareState } from "../types";
import { isCalibrated } from "../utils/calibration";

/**
 * SessionStorageから検査設定を取得
 */
function getExamStateFromStorage(): ExamPrepareState | null {
  try {
    const stored = sessionStorage.getItem("examPrepareState");
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    console.warn("検査設定の読み込みに失敗しました");
  }
  return null;
}

/**
 * SessionStorageに検査設定を保存
 */
function saveExamStateToStorage(state: ExamPrepareState): void {
  try {
    sessionStorage.setItem("examPrepareState", JSON.stringify(state));
  } catch {
    console.warn("検査設定の保存に失敗しました");
  }
}

/**
 * 検査準備ページコンポーネント
 */
export default function ExamPreparePage() {
  const location = useLocation();
  const navigate = useNavigate();

  // location.state または sessionStorage から検査設定を取得
  const [examState, setExamState] = useState<ExamPrepareState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [calibrationWarning, setCalibrationWarning] = useState(false);

  useEffect(() => {
    // location.state を優先、なければ sessionStorage から取得
    const stateFromLocation = location.state as ExamPrepareState | undefined;

    if (stateFromLocation?.patient && stateFromLocation?.settings) {
      setExamState(stateFromLocation);
      saveExamStateToStorage(stateFromLocation);
    } else {
      const storedState = getExamStateFromStorage();
      if (storedState) {
        setExamState(storedState);
      } else {
        setError(
          "検査設定が見つかりません。検査設定画面から開始してください。",
        );
      }
    }

    // キャリブレーション確認
    if (!isCalibrated()) {
      setCalibrationWarning(true);
    }
  }, [location.state]);

  /**
   * 検査開始ボタンハンドラ
   */
  const handleStartExam = () => {
    if (!examState) return;

    // examState を location.state として渡して遷移
    navigate("/exam/run", { state: examState });
  };

  /**
   * 戻るボタンハンドラ
   */
  const handleBack = () => {
    navigate("/exam/settings");
  };

  /**
   * キャリブレーション画面へ
   */
  const handleCalibration = () => {
    // 戻り先を保存
    sessionStorage.setItem("returnAfterCalibration", "/exam/prepare");
    navigate("/calibration");
  };

  // エラー状態
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="text-red-500 mb-4">
            <svg
              className="w-16 h-16 mx-auto"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-4">エラー</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate("/exam/settings")}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            検査設定へ
          </button>
        </div>
      </div>
    );
  }

  // ローディング状態
  if (!examState) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">読み込み中...</div>
      </div>
    );
  }

  const { patient, settings } = examState;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ヘッダー */}
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <h1 className="text-2xl font-bold text-gray-800">検査準備</h1>
        </div>
      </header>

      {/* メインコンテンツ */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* キャリブレーション警告 */}
        {calibrationWarning && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <svg
                className="w-6 h-6 text-amber-500 mr-3 flex-shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <div className="flex-1">
                <h3 className="text-amber-800 font-medium">
                  キャリブレーションが未実施です
                </h3>
                <p className="text-amber-700 text-sm mt-1">
                  正確な測定のため、キャリブレーションを実施することを推奨します。
                  未実施の場合、測定結果の精度が低下する可能性があります。
                </p>
                <button
                  onClick={handleCalibration}
                  className="mt-3 text-amber-800 underline hover:text-amber-900 text-sm font-medium"
                >
                  キャリブレーションを実施する
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 患者情報 */}
        <section className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
            <svg
              className="w-5 h-5 mr-2 text-blue-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              />
            </svg>
            患者情報
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <span className="text-gray-500 text-sm">患者ID</span>
              <p className="text-gray-800 font-medium">{patient.patientCode}</p>
            </div>
            <div>
              <span className="text-gray-500 text-sm">患者名</span>
              <p className="text-gray-800 font-medium">{patient.name}</p>
            </div>
            {patient.nameKana && (
              <div>
                <span className="text-gray-500 text-sm">フリガナ</span>
                <p className="text-gray-800">{patient.nameKana}</p>
              </div>
            )}
          </div>
        </section>

        {/* 検査設定 */}
        <section className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
            <svg
              className="w-5 h-5 mr-2 text-blue-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            検査設定
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <span className="text-gray-500 text-sm">試行回数</span>
              <p className="text-gray-800 font-medium">
                {settings.trialCount} 回
              </p>
            </div>
            <div>
              <span className="text-gray-500 text-sm">線分の長さ</span>
              <p className="text-gray-800 font-medium">
                {settings.lineLengths.join(", ")} mm
              </p>
            </div>
            <div>
              <span className="text-gray-500 text-sm">線分の太さ</span>
              <p className="text-gray-800 font-medium">
                {settings.lineThickness} px
              </p>
            </div>
            <div>
              <span className="text-gray-500 text-sm">異常判定閾値</span>
              <p className="text-gray-800 font-medium">
                {settings.disableAbnormalJudgment
                  ? "判定しない"
                  : `${settings.abnormalThreshold} %`}
              </p>
            </div>
            <div>
              <span className="text-gray-500 text-sm">入力モード</span>
              <p className="text-gray-800 font-medium">
                {settings.inputMode === "touch"
                  ? "タッチ"
                  : settings.inputMode === "pen"
                    ? "ペン"
                    : "マウス"}
              </p>
            </div>
            <div>
              <span className="text-gray-500 text-sm">進捗バー</span>
              <p className="text-gray-800 font-medium">
                {settings.showProgressBar ? "表示" : "非表示"}
              </p>
            </div>
          </div>
        </section>

        {/* 注意事項 */}
        <section className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
          <h2 className="text-lg font-bold text-blue-800 mb-3">検査の流れ</h2>
          <ol className="list-decimal list-inside space-y-2 text-blue-800">
            <li>画面に水平な線分が表示されます</li>
            <li>
              線分の中心だと思う位置をタップ（またはペンでマーク）してください
            </li>
            <li>「次へ」ボタンを押すと次の試行に進みます</li>
            <li>全ての試行が完了すると結果が表示されます</li>
          </ol>
        </section>

        {/* ボタン */}
        <div className="flex justify-between items-center">
          <button
            onClick={handleBack}
            className="px-6 py-3 bg-gray-200 border border-gray-300 rounded text-gray-700 hover:bg-gray-300 font-medium transition-colors"
          >
            戻る
          </button>
          <button
            onClick={handleStartExam}
            className="bg-blue-600 text-white px-10 py-4 rounded-lg font-bold text-lg hover:bg-blue-700 transition-colors shadow-md"
          >
            検査開始
          </button>
        </div>
      </main>
    </div>
  );
}
